import { authFetch } from "@/lib/authFetch";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  DollarSign,
  Tag,
  Clock,
  Shield,
  LogOut,
  Menu,
  X,
  LayoutDashboard,
  Users,
  Send,
  Plus,
  CheckCircle,
  Trash2,
  FolderOpen,
  Search,
  Link as LinkIcon,
  UserPlus,
  Target,
  Layers,
  RotateCcw,
  Award,
  ExternalLink,
  Settings,
  Edit,
  Save,
  XCircle,
  Building2,
  Bookmark,
  CreditCard,
  RefreshCw,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import ClientAIChat from "@/components/ClientAIChat";
import DocumentsTab from "@/components/DocumentsTab";
import CardExplainer, { CARD_EXPLAINERS } from "@/components/CardExplainer";
import { ReadOnly } from "@/components/ReadOnly";
import { ClientLinkHistory } from "@/components/HistoryTimeline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/rRjZ8y0p56UjmV5DCtF1";

// Loan Ready Stages
const LOAN_READY_STAGES = [
  { stage: "Round 1", description: "Initial Credit Review" },
  { stage: "Round 2", description: "Credit Improvement" },
  { stage: "Round 3", description: "Final Review" },
  { stage: "Round 4", description: "Lender Ready" },
];

const CLIENT_STATUSES = [
  "Referral Submitted",
  "Intake Complete",
  "Credit Active",
  "In Progress",
  "Ready for Lender",
  "Loan Ready",
  "Closed Won",
  "Closed Lost",
];

// Status Badge
const StatusBadge = ({ status }) => {
  const statusStyles = {
    "Referral Submitted": "bg-blue-100 text-blue-800 border-blue-200",
    "Intake Complete": "bg-indigo-100 text-indigo-800 border-indigo-200",
    "Credit Active": "bg-amber-100 text-amber-800 border-amber-200",
    "In Progress": "bg-purple-100 text-purple-800 border-purple-200",
    "Ready for Lender": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Loan Ready": "bg-green-100 text-green-800 border-green-200",
    "Closed Won": "bg-green-100 text-green-800 border-green-200",
    "Closed Lost": "bg-red-100 text-red-800 border-red-200",
    default: "bg-slate-100 text-slate-800 border-slate-200",
  };

  return (
    <Badge variant="outline" className={statusStyles[status] || statusStyles.default}>
      {status || "Unknown"}
    </Badge>
  );
};

// Score Card
const ScoreCard = ({ score, label }) => {
  let bgColor = "bg-slate-100";
  let textColor = "text-slate-600";
  
  if (score) {
    if (score >= 700) {
      bgColor = "bg-emerald-100";
      textColor = "text-emerald-700";
    } else if (score >= 650) {
      bgColor = "bg-amber-100";
      textColor = "text-amber-700";
    } else {
      bgColor = "bg-red-100";
      textColor = "text-red-700";
    }
  }

  return (
    <div className={`${bgColor} rounded-lg p-4 text-center`}>
      <p className={`text-3xl font-bold ${textColor}`}>{score || "---"}</p>
      <p className="text-sm text-slate-600 mt-1">{label}</p>
    </div>
  );
};

const ClientDetail = ({ isAdmin }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();

  // Helper to format GHL date values (may be ms timestamp or date string)
  const formatGhlDate = (val) => {
    if (!val || val === "-") return "";
    if (typeof val === "number") {
      return new Date(val).toLocaleDateString();
    }
    if (typeof val === "string" && /^\d{10,13}$/.test(val)) {
      return new Date(parseInt(val)).toLocaleDateString();
    }
    if (typeof val === "string" && val.includes("T")) {
      return new Date(val).toLocaleDateString();
    }
    return val;
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showBookingLink, setShowBookingLink] = useState(false);
  const [salesReps, setSalesReps] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [assigningRep, setAssigningRep] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assigningUser, setAssigningUser] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // (Document upload handled by DocumentsTab component)
  
  // New note state
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState("internal");
  const [submittingNote, setSubmittingNote] = useState(false);
  
  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    due_date: "",
    due_time: "08:00",
    is_recurring: false,
    recurrence_pattern: "",
    assigned_to: "",
    associated_objects: [],
  });
  const [submittingTask, setSubmittingTask] = useState(false);
  
  // Association dialog
  const [associationDialogOpen, setAssociationDialogOpen] = useState(false);
  const [newAssociation, setNewAssociation] = useState({
    association_type: "joint_contact",
    name: "",
    email: "",
    phone: "",
    relationship: "",
  });
  const [submittingAssociation, setSubmittingAssociation] = useState(false);

  useEffect(() => {
    fetchClientData();
    fetchSalesReps();
    fetchPortalUsers();
    fetchAssignedUsers();
  }, [id]);

  const fetchAssignedUsers = async () => {
    try {
      const response = await authFetch(`${API}/clients/${id}/assigned-users`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedUsers(data.assigned_users || []);
      }
    } catch (error) {
      console.error("Error fetching assigned users:", error);
    }
  };

  const handleAssignUser = async (userId) => {
    if (!userId) return;
    setAssigningUser(true);
    try {
      const response = await authFetch(`${API}/clients/${id}/assign-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: userId }),
      });
      if (response.ok) {
        toast.success("User assigned successfully");
        fetchAssignedUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to assign user");
      }
    } catch (error) {
      console.error("Error assigning user:", error);
      toast.error("Failed to assign user");
    } finally {
      setAssigningUser(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      const response = await authFetch(`${API}/clients/${id}/assign-user/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("User removed successfully");
        fetchAssignedUsers();
      } else {
        toast.error("Failed to remove user");
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error("Failed to remove user");
    }
  };

  const fetchClientData = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/clients/${id}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setClient(data.client);
        setNotes(data.notes || []);
        setTasks(data.tasks || []);
        setAuditLogs(data.audit_logs || []);
        setShowBookingLink(data.show_booking_link);
      } else if (response.status === 404) {
        toast.error("Client not found");
        navigate(isAdmin ? "/admin/dashboard" : "/team/dashboard");
      } else if (response.status === 403) {
        toast.error("Access denied");
        navigate(isAdmin ? "/admin/dashboard" : "/team/dashboard");
      }
    } catch (error) {
      console.error("Error fetching client:", error);
      toast.error("Failed to load client data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesReps = async () => {
    try {
      const response = await authFetch(`${API}/lookup/sales-reps`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSalesReps(data.sales_reps);
      }
    } catch (error) {
      console.error("Error fetching sales reps:", error);
    }
  };

  const handleStartEdit = () => {
    setEditData({
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      email: client.email || "",
      phone: client.phone || "",
      date_of_birth: client.date_of_birth || "",
      address1: client.address1 || "",
      city: client.city || "",
      state: client.state || "",
      postal_code: client.postal_code || "",
      betenbough_location: client.betenbough_location || "",
      betenbough_client_id_number: client.betenbough_client_id_number || "",
      betenbough_affiliated_lender: client.betenbough_affiliated_lender || "",
      betenbough_special_notes_or_credit_strategy: client.betenbough_special_notes_or_credit_strategy || "",
      betenbough_deadline_date_for_loan_ready: client.betenbough_deadline_date_for_loan_ready || "",
      betenbough_client_status: client.betenbough_client_status || "",
      loan_ready_stage: client.loan_ready_stage || "",
    });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/clients/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editData),
      });
      if (response.ok) {
        toast.success("Client updated successfully");
        setEditMode(false);
        setHasUnsavedChanges(false);
        // Update client state immediately with edited data
        setClient(prev => ({ ...prev, ...editData }));
        // Also refresh full data
        fetchClientData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update client");
      }
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const fetchPortalUsers = async () => {
    try {
      const response = await authFetch(`${API}/lookup/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPortalUsers(data.users);
      }
    } catch (error) {
      console.error("Error fetching portal users:", error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setSubmittingNote(true);
    try {
      const response = await authFetch(`${API}/clients/${id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note_text: newNote, note_type: noteType }),
      });
      if (response.ok) {
        toast.success(noteType === "client" ? "Client note added" : "Internal note added");
        setNewNote("");
        fetchClientData();
      } else {
        toast.error("Failed to add note");
      }
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title || !newTask.due_date) {
      toast.error("Title and due date are required");
      return;
    }

    setSubmittingTask(true);
    try {
      const response = await authFetch(`${API}/clients/${id}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newTask),
      });
      if (response.ok) {
        toast.success("Task created");
        setTaskDialogOpen(false);
        setNewTask({
          title: "",
          description: "",
          due_date: "",
          due_time: "08:00",
          is_recurring: false,
          recurrence_pattern: "",
          assigned_to: "",
          associated_objects: [],
        });
        fetchClientData();
      } else {
        toast.error("Failed to create task");
      }
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    } finally {
      setSubmittingTask(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const response = await authFetch(`${API}/clients/${id}/tasks/${taskId}?status=completed`, {
        method: "PUT",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Task completed");
        fetchClientData();
      }
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const handleAddAssociation = async () => {
    if (!newAssociation.name) {
      toast.error("Name is required");
      return;
    }

    setSubmittingAssociation(true);
    try {
      const response = await authFetch(`${API}/clients/${id}/associations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newAssociation),
      });
      if (response.ok) {
        toast.success("Association added");
        setAssociationDialogOpen(false);
        setNewAssociation({
          association_type: "joint_contact",
          name: "",
          email: "",
          phone: "",
          relationship: "",
        });
        fetchClientData();
      } else {
        toast.error("Failed to add association");
      }
    } catch (error) {
      console.error("Error adding association:", error);
      toast.error("Failed to add association");
    } finally {
      setSubmittingAssociation(false);
    }
  };

  const handleRemoveAssociation = async (associationId) => {
    try {
      const response = await authFetch(`${API}/clients/${id}/associations/${associationId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Association removed");
        fetchClientData();
      }
    } catch (error) {
      console.error("Error removing association:", error);
    }
  };

  const handleAssignRep = async (salesRep) => {
    setAssigningRep(true);
    try {
      const response = await authFetch(`${API}/clients/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ betenbough_sales_rep: salesRep }),
      });
      if (response.ok) {
        toast.success(`Assigned to ${salesRep}`);
        setClient(prev => ({ ...prev, betenbough_sales_rep: salesRep }));
        fetchClientData();
      } else {
        toast.error("Failed to assign Affiliate 1");
      }
    } catch (error) {
      console.error("Error assigning Affiliate 1:", error);
      toast.error("Failed to assign Affiliate 1");
    } finally {
      setAssigningRep(false);
    }
  };

  // Document upload/download handled by DocumentsTab component

  const basePath = isAdmin ? "/admin" : "/team";

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-slate-50'}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={isAdmin} />

      <main className="flex-1 min-w-0 md:ml-64">
        {/* Top Bar */}
        <header className={`sticky top-0 z-30 px-4 md:px-6 py-4 backdrop-blur-md border-b transition-colors duration-300 ${isDarkMode ? 'bg-black/90 border-[#1a3d18]' : 'bg-white/90 border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className={`md:hidden p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 text-white' : 'hover:bg-slate-100'}`}>
                <Menu className="h-5 w-5" />
              </button>
              <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/clients`)} className={isDarkMode ? 'text-slate-300 hover:text-white hover:bg-[#22c55e]/20' : ''}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {/* Save Changes Button - shows when in edit mode or has unsaved changes */}
              {(editMode || hasUnsavedChanges) && (
                <div className="flex items-center gap-2 mr-4">
                  <Button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="save-changes-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                  {editMode && (
                    <Button
                      variant="outline"
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className={isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : ''}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  )}
                </div>
              )}
              {client && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Affiliate 1:</span>
                    <ReadOnly locked={!["CREDITFIRMINC_ADMIN", "CREDIT_COUNSELOR"].includes(user?.role)} testId="assign-rep-readonly">
                    <Select
                      value={client.betenbough_sales_rep || ""}
                      onValueChange={handleAssignRep}
                      disabled={assigningRep}
                    >
                      <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="assign-rep-select">
                        <SelectValue placeholder="Select Rep" />
                      </SelectTrigger>
                      <SelectContent className={isDarkMode ? 'bg-[#265324] border-[#22c55e]' : ''}>
                        {salesReps.map((rep) => (
                          <SelectItem key={rep} value={rep}>{rep}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    </ReadOnly>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Add User:</span>
                    <Popover open={addUserOpen} onOpenChange={setAddUserOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={addUserOpen}
                          disabled={assigningUser}
                          className={`w-56 justify-between font-normal ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white hover:bg-[#1a3d18] hover:text-white' : ''}`}
                          data-testid="add-user-select"
                        >
                          <span className="truncate text-left text-slate-500">
                            {assigningUser ? "Adding..." : "Select User"}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-0" align="end">
                        <Command>
                          <CommandInput placeholder="Type a name to search..." data-testid="add-user-search-input" />
                          <CommandList>
                            <CommandEmpty>No user found.</CommandEmpty>
                            <CommandGroup>
                              {portalUsers
                                .filter(u => !assignedUsers.some(au => au.user_id === u.user_id))
                                .map((u) => (
                                  <CommandItem
                                    key={u.user_id}
                                    value={`${u.name} ${u.email || ""}`}
                                    onSelect={() => {
                                      handleAssignUser(u.user_id);
                                      setAddUserOpen(false);
                                    }}
                                    data-testid={`add-user-option-${u.user_id}`}
                                  >
                                    <Check className="mr-2 h-4 w-4 opacity-0" />
                                    <div className="flex flex-col">
                                      <span>{u.name}</span>
                                      {u.email && <span className="text-xs text-slate-400">{u.email}</span>}
                                    </div>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              )}
              {showBookingLink && (
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}>
                    <Calendar className="h-4 w-4 mr-2" />
                    Book Counselor
                  </Button>
                </a>
              )}
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6">
          {loading ? (
            <div className="space-y-6">
              <Skeleton className="h-48 w-full rounded-xl" />
              <Skeleton className="h-96 w-full rounded-xl" />
            </div>
          ) : !client ? (
            <Card className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}>
              <CardContent className="py-12 text-center">
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Client not found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6 animate-fade-in">
              {/* Assigned Users Bar - Show if there are assigned users */}
              {assignedUsers.length > 0 && (
                <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-[#265324] border border-[#1a3d18]' : 'bg-slate-50 border border-slate-200'}`}>
                  <div className="flex items-center flex-wrap gap-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Assigned Users:</span>
                    {assignedUsers.map((u) => (
                      <div
                        key={u.user_id}
                        className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1"
                      >
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-5 h-5 rounded-full" />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center text-xs font-medium text-teal-700">
                            {u.name?.charAt(0) || "U"}
                          </div>
                        )}
                        <span className="text-sm text-slate-700">{u.name}</span>
                        <button
                          onClick={() => handleRemoveUser(u.user_id)}
                          className={`ml-1 transition-colors ${isDarkMode ? 'text-slate-400 hover:text-red-400' : 'text-slate-400 hover:text-red-500'}`}
                          title="Remove user"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Client Header Card */}
              <Card data-testid="client-header-card" className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-[#22c55e]/20' : 'bg-teal-100'}`}>
                        <User className={`h-8 w-8 ${isDarkMode ? 'text-[#22c55e]' : 'text-teal-600'}`} />
                      </div>
                      <div>
                        <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          <span className="flex items-center gap-2">
                            {client.first_name} {client.last_name}
                            {client.ghl_contact_id ? (
                              <span className="inline-flex items-center gap-1 text-xs font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full" data-testid="sync-status-ok">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> CRM Synced
                              </span>
                            ) : client.sync_status === "failed" ? (
                              <span className="inline-flex items-center gap-1 text-xs font-normal text-red-600 bg-red-50 px-2 py-0.5 rounded-full" data-testid="sync-status-failed">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Sync Failed
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-normal text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full" data-testid="sync-status-pending">
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" /> Syncing...
                              </span>
                            )}
                          </span>
                        </h1>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <StatusBadge status={client.betenbough_client_status} />
                          {client.betenbough_location && (
                            <Badge variant="outline" className={isDarkMode ? 'bg-[#1a3d18] text-slate-300 border-[#22c55e]' : 'bg-slate-50'}>
                              <MapPin className="h-3 w-3 mr-1" />
                              {client.betenbough_location}
                            </Badge>
                          )}
                          {!client.ghl_contact_id && isAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7 border-red-300 text-red-600 hover:bg-red-50"
                              data-testid="retry-sync-btn"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const res = await authFetch(`${API}/admin/retry-sync/${client.client_id}`, { method: "POST" });
                                  if (res.ok) {
                                    toast.success("Retry sync initiated — refreshing in 15 seconds...");
                                    setTimeout(() => window.location.reload(), 15000);
                                  } else {
                                    toast.error("Retry failed");
                                  }
                                } catch { toast.error("Connection error"); }
                              }}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" /> Retry CRM Sync
                            </Button>
                          )}
                        </div>
                        <div className={`flex flex-wrap gap-4 mt-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          {client.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="h-4 w-4" />
                              {client.email}
                            </div>
                          )}
                          {client.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-4 w-4" />
                              {client.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {client.betenbough_deadline_date_for_loan_ready && (
                        <div className="text-right">
                          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Target Date</p>
                          <p className="text-lg font-semibold text-amber-500">
                            {formatGhlDate(client.betenbough_deadline_date_for_loan_ready)}
                          </p>
                        </div>
                      )}
                      {client.ssn_masked && (
                        <div className={`flex items-center gap-1 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <Shield className="h-4 w-4" />
                          SSN: {client.ssn_masked}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tabs */}
              <Tabs defaultValue="overview" className="space-y-4">
                <TabsList className={`grid w-full grid-cols-3 md:grid-cols-5 h-auto gap-1 ${isDarkMode ? 'bg-[#1a3d18]' : ''}`}>
                  <TabsTrigger value="overview" className={isDarkMode ? 'data-[state=active]:bg-[#22c55e] data-[state=active]:text-white' : ''}>Overview</TabsTrigger>
                  <TabsTrigger value="scores" className={isDarkMode ? 'data-[state=active]:bg-[#22c55e] data-[state=active]:text-white' : ''}>Scores/Reports</TabsTrigger>
                  <TabsTrigger value="progress" className={`flex items-center gap-1.5 ${isDarkMode ? 'data-[state=active]:bg-[#22c55e] data-[state=active]:text-white' : ''}`} data-testid="tab-trigger-progress">
                    Progress Report
                    <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                      (client.opener_positives_ || client.construction_utilization_ || client.constructive_payment_history_if_applicable_and_can_click_multiple || client.closer_positive_statements_ || client.special_note_by_case_manager)
                        ? 'bg-green-500' : 'bg-red-400'
                    }`} data-testid="progress-report-indicator" />
                  </TabsTrigger>
                  <TabsTrigger value="notes" className={isDarkMode ? 'data-[state=active]:bg-[#22c55e] data-[state=active]:text-white' : ''}>Notes</TabsTrigger>
                  <TabsTrigger value="documents" className={isDarkMode ? 'data-[state=active]:bg-[#22c55e] data-[state=active]:text-white' : ''}>Docs</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" data-testid="tab-overview">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* ROW 1: Contact Info */}
                    <Card className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}>
                      <CardHeader>
                        <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Contact Information<CardExplainer text={CARD_EXPLAINERS.contactInfo} isDarkMode={isDarkMode} /></CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Email</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{client.email || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Phone</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{client.phone || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Date of Birth</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{client.date_of_birth || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Address</span>
                          <span className={`font-medium text-right ${isDarkMode ? 'text-white' : ''}`}>
                            {client.address1 ? `${client.address1}, ${client.city || ""} ${client.state || ""} ${client.postal_code || ""}` : "-"}
                          </span>
                        </div>
                      </CardContent>
                    </Card>

                    {/* ROW 1: Partner Details */}
                    <Card className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}>
                      <CardHeader>
                        <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Partner Details<CardExplainer text={CARD_EXPLAINERS.partnerDetails} isDarkMode={isDarkMode} /></CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Location</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{client.location_folder || client.betenbough_locations || client.betenbough_location || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Affiliate Partner</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{client.betenbough_sales_rep || client.affiliate_1_name || client.affiliate_1_full_name || "-"}</span>
                        </div>
                        {(client.affiliate_1_email) && (
                          <div className="flex justify-between">
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Affiliate 1 Email</span>
                            <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : ''}`}>{client.affiliate_1_email}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Affiliate 2</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{client.affiliate_2_name || client.betenbough_affiliate_2 || "-"}</span>
                        </div>
                        {(client.affiliate_2_email) && (
                          <div className="flex justify-between">
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Affiliate 2 Email</span>
                            <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : ''}`}>{client.affiliate_2_email || "-"}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Client ID</span>
                          <span className={`font-medium font-mono text-sm ${isDarkMode ? 'text-white' : ''}`}>{client.betenbough_client_id_number || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Affiliated Lender</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{client.betenbough_affiliated_lender || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Counselor</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{client.assigned_counselor || client.betenbough_assigned_credit_counselor || "-"}</span>
                        </div>
                      </CardContent>
                    </Card>

                    {user?.role === "CREDITFIRMINC_ADMIN" && <ClientLinkHistory clientId={client.client_id} />}

                    {/* ROW 2: Lead/Client Stage */}
                    <Card className={`md:col-span-2 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="lead-client-stage-card">
                      <CardHeader>
                        <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                          <Bookmark className={`h-5 w-5 ${isDarkMode ? 'text-[#22c55e]' : 'text-teal-600'}`} />
                          Lead / Client Stage
                          <CardExplainer text={CARD_EXPLAINERS.leadClientStage} isDarkMode={isDarkMode} />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Lead Entered Date</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="lead-date-created">{formatGhlDate(client.referral_date || client.created_at) || formatGhlDate(client.ghl_created_at) || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Client Start Date</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="client-start-date">{formatGhlDate(client.ghl_created_at || client.client_start_date || client.date_created || client.created_at) || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Lead Stage</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="lead-stage">{client.lead_status_amp_stages || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Client Account Status</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="client-acct-status">{client.client_account_status || client.clients_acct_status || <span className={`text-xs italic font-normal ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data not showing? Ask your AI Assistant</span>}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Credit Program</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="credit-program">{client.credit_program_options || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Payment Status</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="payment-status">{client.payment_status || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Life Plan Payment Status</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="life-plan-payment-status">{client.life_plan_payment_status || "-"}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* ROW 3: Game Plan Recap */}
                    <Card className={`md:col-span-2 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="game-plan-recap-card">
                      <CardHeader>
                        <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                          <FileText className={`h-5 w-5 ${isDarkMode ? 'text-[#22c55e]' : 'text-teal-600'}`} />
                          Game Plan Recap
                          <CardExplainer text={CARD_EXPLAINERS.gamePlanRecap} isDarkMode={isDarkMode} />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Score Projections</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="score-projections">{client.guaranteed_score_length || client.guaranteed_score__length || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Goal</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="client-goal">{client.goal || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Goal Deadline</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="goal-deadline">{formatGhlDate(client.goal_deadlines) || "-"}</p>
                          </div>
                        </div>
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                          <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Game Plan Recap</p>
                          <p className={`whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`} data-testid="game-plan-recap-text">{client.game_plan_recap || "No game plan recap available."}</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* ROW 4: Loan Ready Status */}
                    <Card className={`md:col-span-2 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="loan-ready-status-card">
                      <CardHeader>
                        <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                          <Target className={`h-5 w-5 ${isDarkMode ? 'text-[#22c55e]' : 'text-teal-600'}`} />
                          Loan Ready Status
                          <CardExplainer text={CARD_EXPLAINERS.loanReadyStatus} isDarkMode={isDarkMode} />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loan Ready Stage</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="loan-ready-stage">{client.loan_ready_stage || <span className={`text-xs italic font-normal ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data not showing? Ask your AI Assistant</span>}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loan Ready Grats Stage</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="loan-ready-grats">{client.client_progress || <span className={`text-xs italic font-normal ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data not showing? Ask your AI Assistant</span>}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Rescore Status</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="rescore-status">{client.loan_ready_secondary_status || client.rescore_status || <span className={`text-xs italic font-normal ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data not showing? Ask your AI Assistant</span>}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loan Ready Follow Up Date</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="loan-ready-followup">{formatGhlDate(client.loan_ready_follow_up_date) || <span className={`text-xs italic font-normal ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data not showing? Ask your AI Assistant</span>}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>AU Status</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="au-status">{client.au_status || client.au_status_pick_one || <span className={`text-xs italic font-normal ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data not showing? Ask your AI Assistant</span>}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Selected Loan Ready Actions</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="loan-ready-actions">{client.loan_ready_actions || client.selected_loan_ready_actions || <span className={`text-xs italic font-normal ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data not showing? Ask your AI Assistant</span>}</p>
                          </div>
                          <div className={`md:col-span-2 p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Case Manager Notes</p>
                            <p className={`font-medium whitespace-pre-wrap ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="case-manager-notes">{client.case_manager_notes || <span className={`text-xs italic font-normal ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data not showing? Ask your AI Assistant</span>}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* ROW 5: Restoration Process */}
                    <Card className={`md:col-span-2 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="restoration-process-card">
                      <CardHeader className="pb-2">
                        <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                          <RotateCcw className={`h-5 w-5 ${isDarkMode ? 'text-[#c3ca6d]' : 'text-[#1b4541]'}`} />
                          Restoration Process
                          <CardExplainer text={CARD_EXPLAINERS.restorationProcess} isDarkMode={isDarkMode} />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Current Round</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="current-round">{client.rounds || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Round Date</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="round-date">{formatGhlDate(client.round_date || client.round_date_due_for_update_in_50days_from_this_date) || "-"}</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Days Till Update</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="days-till-update">{
                              client.days_till_due || (() => {
                                const rd = client.round_date || client.round_date_due_for_update_in_50days_from_this_date;
                                if (!rd) return "-";
                                const roundDate = typeof rd === "number" ? new Date(rd) : new Date(rd);
                                const dueDate = new Date(roundDate.getTime() + 50 * 24 * 60 * 60 * 1000);
                                const daysLeft = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
                                return daysLeft > 0 ? `${daysLeft} days` : daysLeft === 0 ? "Due today" : "Past due";
                              })()
                            }</p>
                          </div>
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>PR Status</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="pr-status">{client.pr_status || "-"}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* ROW 6: Client Settlement Reserve Account (CSRA) */}
                    <Card className={`md:col-span-2 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="csra-details-card">
                      <CardHeader className="pb-2">
                        <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                          <DollarSign className={`h-5 w-5 ${isDarkMode ? 'text-[#c3ca6d]' : 'text-[#1b4541]'}`} />
                          Client Settlement Reserve Account (CSRA)
                          <CardExplainer text={CARD_EXPLAINERS.csra} isDarkMode={isDarkMode} />
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* CSRA Overview */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Estimated Settlement</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="csra-total-settlement">{client.total_estimated_settlement || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>CSRA Current Balance</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="csra-current-balance">{client.csra_current_balance || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>CSRA Payment Terms & Instructions</p>
                            <p className={`font-medium whitespace-pre-wrap ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="csra-payment-terms">{client.csra_payment_terms_and_instructions || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>CSRA Payment Status</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="csra-payment-status">{client.csra_payment_status || "-"}</p>
                          </div>
                        </div>

                        {/* Registered Furnishers 1-5 */}
                        {[1,2,3,4,5].map((num) => {
                          const info = client[`csra_furnisher_${num}_info`];
                          const est = client[`csra_furnisher_${num}_estimated_settlement`];
                          const status = client[`csra_furnisher_${num}_status`];
                          if (!info && !est && !status) return null;
                          return (
                            <div key={num} className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`} data-testid={`csra-furnisher-${num}`}>
                              <p className={`text-xs font-bold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-[#c3ca6d]' : 'text-[#1b4541]'}`}>Furnisher #{num}</p>
                              <div className="grid md:grid-cols-3 gap-3">
                                <div>
                                  <p className={`text-xs mb-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Name, Account # & Amount</p>
                                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{info || "-"}</p>
                                </div>
                                <div>
                                  <p className={`text-xs mb-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Estimated Settlement</p>
                                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{est || "-"}</p>
                                </div>
                                <div>
                                  <p className={`text-xs mb-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Status</p>
                                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{status || "-"}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>

                    {/* ROW 7: BH Internal Status Notes */}
                    <Card className={`md:col-span-2 ${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                          <Building2 className={`h-5 w-5 ${isDarkMode ? 'text-[#c3ca6d]' : 'text-[#1b4541]'}`} />
                          BH Internal Status Notes
                          <CardExplainer text={CARD_EXPLAINERS.bhInternalNotes} isDarkMode={isDarkMode} />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>BH Location</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{client.betenbough_locations || client.betenbough_location || client.location_folder || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Betenbough Status</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="bh-status">{client.betenbough_status || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>BH Client Card ID Number</p>
                            <p className={`font-medium font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{client.betenbough_client_card_number || client.betenbough_client_id_number || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Review Date / PreQual Sent Date</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{formatGhlDate(client.close_date_review_date_or_prequal_sent_date || client.close_date_review_date_or_prequal_sent_dte) || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Close Date or Est Close Date</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{formatGhlDate(client.close_date_or_est_close_date) || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Home Tour</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{client.home_tour || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Lender Review</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{client.lender_review || "-"}</p>
                          </div>
                          <div className={`md:col-span-2 p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Comments for Betenbough File</p>
                            <p className={`font-medium whitespace-pre-wrap ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="bh-comments">{client.comments_for_betenbough_file || client.betenbough_special_notes_or_credit_strategy || "-"}</p>
                          </div>
                          <div className={`md:col-span-2 p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Preferred Lender Notes</p>
                            <p className={`font-medium whitespace-pre-wrap ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="lender-notes">{client.affiliate_note || client.preferred_lender_notes || "-"}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Scores/Reports Tab */}
                <TabsContent value="scores" data-testid="tab-scores">
                  <div className="space-y-6">
                    <Card className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}>
                      <CardHeader>
                        <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Credit Scores<CardExplainer text={CARD_EXPLAINERS.creditScores} isDarkMode={isDarkMode} /></CardTitle>
                        <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>Scores from the three major bureaus</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4">
                          <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Current Scores</p>
                          <div className="grid grid-cols-3 gap-4">
                            <ScoreCard score={client.transunion_score || client.orig_transunion_score} label="TransUnion" />
                            <ScoreCard score={client.equifax_score || client.orig_equifax_score} label="Equifax" />
                            <ScoreCard score={client.experian_score || client.orig_experian_score} label="Experian" />
                          </div>
                        </div>
                        {(client.orig_transunion_score || client.orig_equifax_score || client.orig_experian_score) && (
                          <div className="mb-4">
                            <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Initial Scores (at enrollment)</p>
                            <div className="grid grid-cols-3 gap-4">
                              <ScoreCard score={client.orig_transunion_score} label="TransUnion" />
                              <ScoreCard score={client.orig_equifax_score} label="Equifax" />
                              <ScoreCard score={client.orig_experian_score} label="Experian" />
                            </div>
                          </div>
                        )}
                        {client.lender_scores && (
                          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}><strong>Lender Scores:</strong> {client.lender_scores}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Life Plan Monitoring Link & Credentials - moved here from Overview */}
                    <Card className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''} data-testid="life-plan-monitoring-card">
                      <CardHeader className="pb-2">
                        <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                          <Shield className={`h-5 w-5 ${isDarkMode ? 'text-[#c3ca6d]' : 'text-[#1b4541]'}`} />
                          Life Plan Monitoring Link & Credentials
                          <CardExplainer text={CARD_EXPLAINERS.lifePlanMonitoring} isDarkMode={isDarkMode} />
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Provider</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="monitoring-provider">{client.monitoring_provider || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>User Name</p>
                            <p className={`font-medium font-mono text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="monitoring-username">{client.user_name || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Monitoring Password</p>
                            <p className={`font-medium font-mono text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="monitoring-password">{client.monitoring_password || "-"}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Life Plan Payment Status</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{client.life_plan_payment_status || "-"}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Progress Report Tab */}
                <TabsContent value="progress" data-testid="tab-progress">
                  <div className="space-y-6">
                  {/* Restoration Process Card (duplicated from Overview) */}
                  <Card className={`${isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}`} data-testid="progress-restoration-process-card">
                    <CardHeader className="pb-2">
                      <CardTitle className={`text-lg flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                        <RotateCcw className={`h-5 w-5 ${isDarkMode ? 'text-[#c3ca6d]' : 'text-[#1b4541]'}`} />
                        Restoration Process
                        <CardExplainer text={CARD_EXPLAINERS.restorationProcess} isDarkMode={isDarkMode} />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                          <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Current Round</p>
                          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{client.rounds || "-"}</p>
                        </div>
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                          <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Round Date</p>
                          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{formatGhlDate(client.round_date || client.round_date_due_for_update_in_50days_from_this_date) || "-"}</p>
                        </div>
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                          <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Days Till Update</p>
                          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{
                            client.days_till_due || (() => {
                              const rd = client.round_date || client.round_date_due_for_update_in_50days_from_this_date;
                              if (!rd) return "-";
                              const roundDate = typeof rd === "number" ? new Date(rd) : new Date(rd);
                              const dueDate = new Date(roundDate.getTime() + 50 * 24 * 60 * 60 * 1000);
                              const daysLeft = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
                              return daysLeft > 0 ? `${daysLeft} days` : daysLeft === 0 ? "Due today" : "Past due";
                            })()
                          }</p>
                        </div>
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-50'}`}>
                          <p className={`text-xs mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>PR Status</p>
                          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{client.pr_status || "-"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Progress Report Card */}
                  <Card className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''} data-testid="progress-report-card">
                    <CardHeader className="pb-4">
                      <CardTitle className={`text-xl flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                        <FileText className={`h-5 w-5 ${isDarkMode ? 'text-[#c3ca6d]' : 'text-[#1b4541]'}`} />
                        CFI Services Progress Report
                        <CardExplainer text={CARD_EXPLAINERS.progressReport} isDarkMode={isDarkMode} />
                        {(client.opener_positives_ || client.construction_utilization_ || client.constructive_payment_history_if_applicable_and_can_click_multiple || client.closer_positive_statements_ || client.special_note_by_case_manager) ? (
                          <Badge className="bg-green-500/15 text-green-700 border-green-300 text-xs ml-2">Report Available</Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-400 border-red-300 text-xs ml-2">Pending</Badge>
                        )}
                      </CardTitle>
                      <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {client.first_name} {client.last_name}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        as of {formatGhlDate(client.round_date || client.round_date_due_for_update_in_50days_from_this_date) || "N/A"}
                      </p>
                      {client.rx_or_case_manager_name_ && (
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Case Manager: <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{client.rx_or_case_manager_name_}</span>
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-8">

                      {/* Greeting */}
                      <div className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                        <p>Hello <strong>{client.first_name} {client.last_name}</strong>,</p>
                        <p className="mt-3">As discussed in your initial consultation, it will take anywhere from <strong>45-60 days</strong> for a round of letters to be responded to by the creditor.</p>
                        <p className="mt-3">Below is a progress report of the most recent round sent. You should see all three major credit bureaus with your current scores.</p>
                        <p className="mt-3">Please also pay close attention to the <strong>Case Manager's Notes</strong>. In these notes, the counselor will advise on any adjustments or concerns regarding your score and its progress. Don't hesitate to call your counselor with any further questions you may have.</p>
                      </div>

                      {/* Warning */}
                      <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? 'bg-amber-900/20 border-amber-500' : 'bg-amber-50 border-amber-500'}`}>
                        <p className={`text-xs font-bold uppercase leading-relaxed ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                          THESE ARE MONITORING SCORES. LENDER SCORES WILL BE DIFFERENT. BEFORE YOU HAVE A LENDER PULL OR LOOK AT YOUR CREDIT FILE, SCHEDULE A REVIEW WITH YOUR COUNSELOR. THIS IS TO AVOID PULLING INQUIRIES DURING REPAIR; THESE SCORES ARE NOT THE FICO CLASSIC MODEL.
                        </p>
                      </div>

                      {/* ── Credit Scores ── */}
                      <div>
                        <h3 className={`text-base font-bold uppercase tracking-wide mb-4 pb-2 border-b-2 ${isDarkMode ? 'text-white border-[#22c55e]' : 'text-slate-900 border-teal-600'}`}>
                          Current Credit Scores
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                          {[
                            { label: "Experian", score: client.experian_score || client.orig_experian_score },
                            { label: "Equifax", score: client.equifax_score || client.orig_equifax_score },
                            { label: "TransUnion", score: client.transunion_score || client.orig_transunion_score },
                          ].map(({ label, score }) => (
                            <div key={label} className={`p-5 rounded-lg text-center border ${isDarkMode ? 'bg-black/30 border-[#22c55e]/30' : 'bg-white border-slate-200 shadow-sm'}`}>
                              <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
                              <p className={`text-3xl font-bold ${score ? (parseInt(score) >= 700 ? 'text-emerald-500' : parseInt(score) >= 650 ? 'text-amber-500' : 'text-red-500') : (isDarkMode ? 'text-slate-500' : 'text-slate-300')}`}>
                                {score || "---"}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* ── Case Manager Review ── */}
                      <div>
                        <h3 className={`text-base font-bold uppercase tracking-wide mb-3 pb-2 border-b-2 ${isDarkMode ? 'text-white border-[#22c55e]' : 'text-slate-900 border-teal-600'}`}>
                          Case Manager Review
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                          Please read and review highlights of your progress as well as any important notes regarding your restoration process. Reviews will specifically highlight the major parts of your scores and how you are doing in each part. Below will be broken down to these parts: <strong>General Overview, Utilization, Payment History, Next Move</strong>, as well as any specific notes.
                        </p>
                      </div>

                      {/* ── GENERAL PROGRESS ── */}
                      <div>
                        <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          <span className="inline-block w-1.5 h-5 rounded bg-emerald-500" />
                          General Progress
                        </h4>
                        <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? 'bg-emerald-900/15 border-emerald-500' : 'bg-emerald-50 border-emerald-500'}`} data-testid="pr-opener-positives">
                          <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                            {client.opener_positives_ || <span className={`italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No data available yet for this section.</span>}
                          </p>
                        </div>
                      </div>

                      {/* ── UTILIZATION ── */}
                      <div>
                        <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          <span className="inline-block w-1.5 h-5 rounded bg-blue-500" />
                          Utilization
                        </h4>
                        <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                          Let's talk about your utilization — this is a huge part of the score as it makes up to <strong>35% of the overall score</strong>. It simply looks at the reporting balances of your credit cards versus your available limits, determining what percentage is being utilized and not paid off. The lower that percentage, the better! See notes from your case manager below in regards to your utilization.
                        </p>
                        <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? 'bg-blue-900/15 border-blue-500' : 'bg-blue-50 border-blue-500'}`} data-testid="pr-construction-utilization">
                          <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                            {client.construction_utilization_ || <span className={`italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No data available yet for this section.</span>}
                          </p>
                        </div>
                      </div>

                      {/* ── PAYMENT HISTORY ── */}
                      <div>
                        <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          <span className="inline-block w-1.5 h-5 rounded bg-purple-500" />
                          Payment History
                        </h4>
                        <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                          <strong>45% of your score</strong> — might as well call it half! This part of the score looks at account status from a month-to-month perspective, which includes late payments, collections, and charged-offs. So important to stay current and avoid new collections going forward while we focus on cleaning up the past. Below is the case manager's review of your payment history.
                        </p>
                        <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? 'bg-purple-900/15 border-purple-500' : 'bg-purple-50 border-purple-500'}`} data-testid="pr-payment-history">
                          <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                            {client.constructive_payment_history_if_applicable_and_can_click_multiple || <span className={`italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No data available yet for this section.</span>}
                          </p>
                        </div>
                      </div>

                      {/* ── NEXT MOVE ── */}
                      <div>
                        <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          <span className="inline-block w-1.5 h-5 rounded bg-amber-500" />
                          Next Move
                        </h4>
                        <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                          Like we talked about, this is a process — each round either results in a deletion/update or builds leverage that positions us to strong-arm accordingly. Below highlights where we are at in that process as we progress:
                        </p>
                        <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? 'bg-amber-900/15 border-amber-500' : 'bg-amber-50 border-amber-500'}`} data-testid="pr-closer-statements">
                          <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                            {client.closer_positive_statements_ || <span className={`italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No data available yet for this section.</span>}
                          </p>
                        </div>
                      </div>

                      {/* ── DETAIL NOTES ── */}
                      <div>
                        <h4 className={`font-bold uppercase tracking-wide mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          <span className="inline-block w-1.5 h-5 rounded bg-rose-500" />
                          Detail Notes
                        </h4>
                        <div className={`p-4 rounded-lg border-l-4 ${isDarkMode ? 'bg-rose-900/15 border-rose-500' : 'bg-rose-50 border-rose-500'}`} data-testid="pr-special-note">
                          <p className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                            {client.special_note_by_case_manager || <span className={`italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No detail notes from case manager at this time.</span>}
                          </p>
                        </div>
                      </div>

                      {/* ── ACCESS FULL REPORT ── */}
                      <div>
                        <h3 className={`text-base font-bold uppercase tracking-wide mb-3 pb-2 border-b-2 ${isDarkMode ? 'text-white border-[#22c55e]' : 'text-slate-900 border-teal-600'}`}>
                          Access Your Full Report
                        </h3>
                        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-[#22c55e]/10 border-[#22c55e]/30' : 'bg-teal-50 border-teal-200'}`}>
                          <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Website: <a href="https://cfilifeplan.com" target="_blank" rel="noopener noreferrer" className={`underline ${isDarkMode ? 'text-[#22c55e]' : 'text-teal-700'}`}>cfilifeplan.com</a>
                          </p>
                          <p className={`mt-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}><strong>Username:</strong> {client.user_name || "-"}</p>
                          <p className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}><strong>Password:</strong> credit1</p>
                        </div>
                      </div>

                      {/* ── Closing Text ── */}
                      <div className={`space-y-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        <p>
                          Just as a reminder, please continue to send us your <strong>correspondence from the credit bureaus</strong> and any collection agencies. Also, remember to bring in any <strong>dunning notices</strong> as soon as possible. These are collection letters that start with or contain the following language:
                        </p>
                        <blockquote className={`pl-4 border-l-2 italic ${isDarkMode ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-500'}`}>
                          "Unless you notify this office within 30 days after receiving this notice that you dispute the validity of this debt or any portion thereof, our office will assume the debt is valid..."
                        </blockquote>
                        <p>
                          Please continue to <strong>pay everything on time</strong> and manage your credit cards accordingly. If your counselor specified anything specific in your Client Responsibility Contract, please handle it accordingly or call if your circumstances have changed.
                        </p>
                        <p>
                          The plan will work, and you will achieve your goals. Your goals are our goals, and we'll meet them together. Our credit restoration program is different from all the rest, so do not listen to the cynics regarding credit repair. We ask that you continue to have the same faith with us as you had when you started. To my knowledge, there is not one credit repair organization that has come even close to the success ratios that we have.
                        </p>
                        <blockquote className={`pl-4 border-l-2 italic ${isDarkMode ? 'border-slate-600 text-slate-400' : 'border-slate-300 text-slate-500'}`}>
                          As discussed in your initial consultation, any remaining derogatory items on your credit report are being worked on, and a case is being built against them. Many derogatory items will come off quickly while others will take more time. Thanks again for your confidence in us, and we look forward to helping you meet your financial goals.
                        </blockquote>
                      </div>

                      {/* Signoff */}
                      <div className={`pt-4 border-t ${isDarkMode ? 'border-slate-600' : 'border-slate-300'}`}>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Sincerely,</p>
                        <p className={`font-bold text-lg ${isDarkMode ? 'text-[#22c55e]' : 'text-teal-700'}`}>Credit Firm Inc</p>
                      </div>

                    </CardContent>
                  </Card>
                  </div>
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" data-testid="tab-notes">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Notes</CardTitle>
                      <CardDescription>Internal and client-visible notes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Add Note */}
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Select value={noteType} onValueChange={setNoteType}>
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="internal">Add Internal Note</SelectItem>
                                <SelectItem value="client">Add Client Note</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex gap-2">
                            <Textarea
                              placeholder={noteType === "client" ? "Add a note visible to the client..." : "Add an internal note (only visible to staff)..."}
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              className="flex-1"
                              data-testid="note-input"
                            />
                            <Button onClick={handleAddNote} disabled={submittingNote || !newNote.trim()} data-testid="add-note-btn">
                              <Send className="h-4 w-4" />
                            </Button>
                          </div>
                          {noteType === "client" && (
                            <p className="text-xs text-blue-600">This note will be visible to the client in their portal and tagged in the CRM</p>
                          )}
                          {noteType === "internal" && (
                            <p className="text-xs text-slate-500">This note is only visible to staff members</p>
                          )}
                        </div>

                        {/* Notes List */}
                        {notes.length > 0 ? (
                          <div className="space-y-3">
                            {notes.map((note) => (
                              <div key={note.note_id} className={`p-4 rounded-lg ${
                                note.note_type === "client" ? "bg-blue-50 border border-blue-200" : "bg-slate-50"
                              }`}>
                                <div className="flex items-start justify-between">
                                  <div>
                                    {note.note_type === "client" && (
                                      <Badge variant="outline" className="bg-blue-100 text-blue-800 mb-2">Client Note</Badge>
                                    )}
                                    {note.note_type === "internal" && (
                                      <Badge variant="outline" className="bg-slate-100 text-slate-800 mb-2">Internal Note</Badge>
                                    )}
                                    <p className="text-slate-700">{note.note_text}</p>
                                  </div>
                                </div>
                                <p className="text-xs text-slate-500 mt-2">
                                  {note.user_name && `${note.user_name} • `}{new Date(note.created_at).toLocaleString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-center py-4">No notes yet</p>
                        )}

                        {/* Counselor Note (from GHL) */}
                        {client.counselor_note && (
                          <div className="mt-6">
                            <h4 className="text-sm font-medium text-slate-900 mb-2">Counselor Note</h4>
                            <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                              <p className="text-slate-700">{client.counselor_note}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" data-testid="tab-documents">
                  <DocumentsTab clientId={id} isAdmin={isAdmin} isDarkMode={isDarkMode} />
                </TabsContent>

              </Tabs>
            </div>
          )}
        </div>
      </main>

      {/* AI Chat Bot */}
      {client && (
        <ClientAIChat
          clientId={id}
          firstName={client.first_name || "this client"}
        />
      )}
    </div>
  );
};

export default ClientDetail;
